import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import { ShieldQuestion, AlertTriangle, ShieldCheck, Crosshair } from "lucide-react";

const CONTEXT_KEY = "__context__";

const FAMILY_META: Record<string, { label: string; icon: any; hint: string }> = {
  security: { label: "Security & readiness", icon: ShieldCheck, hint: "DISP / Essential Eight / security opportunity signals" },
  defence_intent: { label: "Defence Intent triggers", icon: Crosshair, hint: "Analyst-only — evidence of Defence market intent" },
};

const CATEGORY_LABEL: Record<string, string> = {
  discovery_signal: "Discovery Signal",
  qualification_evidence: "Qualification Evidence",
};

// Category is derived from the evidence type's group, never chosen freely (except context-only).
function categoryForFamily(family?: string): "discovery_signal" | "qualification_evidence" | null {
  if (family === "defence_intent") return "discovery_signal";
  if (family === "security") return "qualification_evidence";
  return null;
}

type SharedFields = {
  evidence_class: string;
  event_date: string;
  source_title: string;
  evidence_text: string;
};

function blankShared(catalogue: any): SharedFields {
  return {
    evidence_class: (catalogue?.evidence_classes || ["Prospect-Confirmed"])[0],
    event_date: new Date().toISOString().slice(0, 10),
    source_title: "",
    evidence_text: "",
  };
}

function ClassDate({ value, onChange, prefix, catalogue }: { value: SharedFields; onChange: (p: Partial<SharedFields>) => void; prefix: string; catalogue: any }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <Label>Evidence class</Label>
        <Select value={value.evidence_class} onValueChange={(v) => onChange({ evidence_class: v })}>
          <SelectTrigger data-testid={`${prefix}-evidence-class`}><SelectValue /></SelectTrigger>
          <SelectContent>{(catalogue?.evidence_classes || ["Prospect-Confirmed"]).map((cl: string) => <SelectItem key={cl} value={cl}>{cl}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Event date</Label>
        <DatePicker testid={`${prefix}-date`} value={value.event_date} onChange={(v) => onChange({ event_date: v })} placeholder="Select event date" />
      </div>
    </div>
  );
}

export function AddEvidenceDialog({
  open, onOpenChange, catalogue, companyId, onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  catalogue: any;
  companyId: string;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [mode, setMode] = useState<"all" | "individual">("all");
  const [shared, setShared] = useState<SharedFields>(blankShared(catalogue));
  const [perItem, setPerItem] = useState<Record<string, Partial<SharedFields>>>({});
  const [contextCategory, setContextCategory] = useState<string>("qualification_evidence");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected({});
      setMode("all");
      setShared(blankShared(catalogue));
      setPerItem({});
      setContextCategory("qualification_evidence");
      setErrors({});
    }
  }, [open, catalogue]);

  const groups = useMemo(() => {
    const g: Record<string, any[]> = { security: [], defence_intent: [] };
    (catalogue?.signal_types || []).forEach((s: any) => {
      if (g[s.family]) g[s.family].push(s);
    });
    return g;
  }, [catalogue]);

  const labelFor = (key: string) => {
    if (key === CONTEXT_KEY) return catalogue?.context_only_label || "Context only – no score";
    const s = (catalogue?.signal_types || []).find((x: any) => x.key === key);
    return s?.label || key;
  };

  const familyOf = (key: string): string | undefined => {
    if (key === CONTEXT_KEY) return "context";
    return (catalogue?.signal_types || []).find((x: any) => x.key === key)?.family;
  };

  // Category is auto-derived from the group; context-only is the sole manual path.
  const categoryFor = (key: string): string =>
    key === CONTEXT_KEY ? contextCategory : (categoryForFamily(familyOf(key)) || "qualification_evidence");

  const renderCategory = (key: string) =>
    key === CONTEXT_KEY ? (
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-slate-400">Category</span>
        <Select value={contextCategory} onValueChange={setContextCategory}>
          <SelectTrigger className="h-7 w-[190px]" data-testid="context-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="qualification_evidence">Qualification Evidence</SelectItem>
            <SelectItem value="discovery_signal">Discovery Signal</SelectItem>
          </SelectContent>
        </Select>
      </div>
    ) : (
      <span data-testid={`auto-category-${key}`}
        className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600 whitespace-nowrap">
        {CATEGORY_LABEL[categoryFor(key)]}
      </span>
    );

  const orderedSelected = useMemo(() => {
    const keys: string[] = [];
    ["security", "defence_intent"].forEach((fam) => groups[fam].forEach((s: any) => { if (selected[s.key]) keys.push(s.key); }));
    if (selected[CONTEXT_KEY]) keys.push(CONTEXT_KEY);
    return keys;
  }, [selected, groups]);

  const toggle = (key: string) =>
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));

  const resolved = (key: string): SharedFields =>
    mode === "individual" ? { ...shared, ...(perItem[key] || {}) } : shared;

  const updateShared = (patch: Partial<SharedFields>) => setShared((s) => ({ ...s, ...patch }));
  const updatePer = (key: string, patch: Partial<SharedFields>) =>
    setPerItem((p) => ({ ...p, [key]: { ...(p[key] || {}), ...patch } }));

  const submit = async () => {
    if (orderedSelected.length === 0) { toast.error("Select at least one evidence type"); return; }
    const localErrors: Record<string, string> = {};
    orderedSelected.forEach((key) => {
      const f = resolved(key);
      if (!f.source_title.trim() || !f.evidence_text.trim()) localErrors[key] = "Source and details are required";
    });
    if (Object.keys(localErrors).length) {
      setErrors(localErrors);
      if (mode === "all") toast.error("Add a source and details before saving");
      else toast.error("Fix the flagged items before saving");
      return;
    }
    const items = orderedSelected.map((key) => {
      const f = resolved(key);
      return {
        signal_type: key === CONTEXT_KEY ? null : key,
        evidence_class: f.evidence_class,
        evidence_category: categoryFor(key),
        source_title: f.source_title,
        evidence_text: f.evidence_text,
        event_date: f.event_date,
      };
    });
    setSubmitting(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/evidence/batch`, { items });
      toast.success(`${data.captured} evidence item${data.captured === 1 ? "" : "s"} captured and re-scored`);
      onOpenChange(false);
      onSaved();
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        const errs: Record<string, string> = {};
        detail.errors.forEach((x: any) => { const k = orderedSelected[x.row - 1]; if (k) errs[k] = x.error; });
        setErrors(errs);
        if (mode === "all") setMode("individual");
        toast.error(detail.message || "Fix the flagged items before saving");
      } else { toast.error(apiError(detail)); }
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="discovery-dialog">
        <DialogHeader>
          <DialogTitle>Add Evidence</DialogTitle>
          <DialogDescription>
            Select every evidence type this discovery surfaced, then apply the details to all of them at once — or set them individually. Each captured item keeps its own provenance and all are re-scored together.
            {catalogue?.sales_restricted && " Sales may log Security/readiness needs or context only — Defence Intent triggers require Analyst review."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Step 1 — matrix of evidence types */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">1 · Select evidence types</div>
            <div className="space-y-3">
              {["security", "defence_intent"].map((fam) => {
                const items = groups[fam];
                if (!items || items.length === 0) return null;
                const Meta = FAMILY_META[fam];
                const Icon = Meta.icon;
                return (
                  <div key={fam} className="rounded-md border border-slate-200 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-4 w-4 text-teal-700" />
                      <span className="text-sm font-medium text-slate-800">{Meta.label}</span>
                      <span className="text-[11px] text-slate-400">· {Meta.hint}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                      {items.map((s: any) => {
                        const on = !!selected[s.key];
                        return (
                          <label key={s.key} data-testid={`type-option-${s.key}`}
                            className={`flex items-start gap-2 rounded-md border px-2.5 py-2 cursor-pointer transition-colors ${on ? "border-teal-500 bg-teal-50/60" : "border-slate-200 hover:bg-slate-50"} ${errors[s.key] ? "border-red-300" : ""}`}>
                            <Checkbox checked={on} onCheckedChange={() => toggle(s.key)} data-testid={`type-checkbox-${s.key}`} className="mt-0.5" />
                            <span className="text-sm text-slate-700 leading-snug">{s.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {/* Context only */}
              <label data-testid={`type-option-${CONTEXT_KEY}`}
                className={`flex items-center gap-2 rounded-md border px-2.5 py-2 cursor-pointer transition-colors ${selected[CONTEXT_KEY] ? "border-slate-400 bg-slate-100" : "border-slate-200 hover:bg-slate-50"}`}>
                <Checkbox checked={!!selected[CONTEXT_KEY]} onCheckedChange={() => toggle(CONTEXT_KEY)} data-testid={`type-checkbox-${CONTEXT_KEY}`} />
                <span className="text-sm text-slate-600">{catalogue?.context_only_label || "Context only – no score"}</span>
              </label>
            </div>
          </div>

          {/* Step 2 — details */}
          {orderedSelected.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  2 · Details for {orderedSelected.length} selected item{orderedSelected.length > 1 ? "s" : ""}
                </div>
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="detail-mode-toggle">
                  <button type="button" onClick={() => setMode("all")} data-testid="mode-all"
                    className={`px-3 py-1 text-xs transition-colors ${mode === "all" ? "bg-[#0B1C3D] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Apply to all</button>
                  <button type="button" onClick={() => setMode("individual")} data-testid="mode-individual"
                    className={`px-3 py-1 text-xs transition-colors ${mode === "individual" ? "bg-[#0B1C3D] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Set each individually</button>
                </div>
              </div>

              {mode === "all" ? (
                <div className="rounded-md border border-slate-200 p-3 space-y-3" data-testid="shared-details">
                  <div className="space-y-1.5" data-testid="category-summary">
                    {orderedSelected.map((key) => (
                      <div key={key} className="flex items-center justify-between gap-3">
                        <span className="text-sm text-slate-700">{labelFor(key)}</span>
                        {renderCategory(key)}
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-slate-100 pt-3 space-y-3">
                    <ClassDate value={shared} onChange={updateShared} prefix="shared" catalogue={catalogue} />
                    <div>
                      <Label>Source / who said it</Label>
                      <Input data-testid="shared-source" value={shared.source_title} onChange={(e) => updateShared({ source_title: e.target.value })} placeholder="e.g. Discovery call with Head of Security" />
                    </div>
                    <div>
                      <Label>What was stated</Label>
                      <Textarea data-testid="shared-text" value={shared.evidence_text} onChange={(e) => updateShared({ evidence_text: e.target.value })}
                        placeholder="Prospect-stated need / interest. Do NOT record PROTECTED/classified, clearance or FOCI detail." />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {orderedSelected.map((key) => {
                    const f = resolved(key);
                    return (
                      <div key={key} className={`rounded-md border p-3 space-y-3 ${errors[key] ? "border-red-300 bg-red-50/40" : "border-slate-200"}`} data-testid={`item-details-${key}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-medium text-slate-800">{labelFor(key)}</div>
                          {renderCategory(key)}
                        </div>
                        <ClassDate value={f} onChange={(p) => updatePer(key, p)} prefix={`item-${key}`} catalogue={catalogue} />
                        <div>
                          <Label>Source / who said it</Label>
                          <Input data-testid={`item-source-${key}`} value={f.source_title} onChange={(e) => updatePer(key, { source_title: e.target.value })} placeholder="e.g. Discovery call with Head of Security" />
                        </div>
                        <div>
                          <Label>What was stated</Label>
                          <Textarea data-testid={`item-text-${key}`} value={f.evidence_text} onChange={(e) => updatePer(key, { evidence_text: e.target.value })}
                            placeholder="Prospect-stated need / interest. Do NOT record PROTECTED/classified, clearance or FOCI detail." />
                        </div>
                        {errors[key] && (
                          <div className="text-xs text-red-600 flex items-center gap-1" data-testid={`item-error-${key}`}>
                            <AlertTriangle className="h-3.5 w-3.5" /> {errors[key]}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            <ShieldQuestion className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Unclassified internal system. Absence of evidence is a research state, never a gap. Only attributable, prospect-stated needs are captured here.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#0B1C3D] hover:bg-[#122a55]" onClick={submit} disabled={submitting || orderedSelected.length === 0} data-testid="dc-submit">
            {submitting ? "Saving…" : `Capture ${orderedSelected.length} item${orderedSelected.length === 1 ? "" : "s"} & re-score`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
